import { useEffect, useState } from "react";
import { InfoCircle, Warning2 } from "iconsax-react";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";
import Button from "../Button";

interface Props {
  color: "positive" | "negative" | "notice" | "grey";
  text: string;
  additionalText?: string;
  isFullWidth?: boolean;
  onClose?: () => void;
  closable?: boolean;
  autoClose?: boolean;
  autoCloseDuration?: number;
  action?: { label: string; onClick: () => void };
}

const colorClasses: Record<Props["color"], { container: string; closeBtn: string }> = {
  positive: {
    container: "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-200",
    closeBtn: "text-green-900 dark:text-green-200",
  },
  negative: {
    container: "bg-red-100 text-red-900 dark:bg-red-900 dark:text-red-200",
    closeBtn: "text-red-900 dark:text-red-200",
  },
  notice: {
    container: "bg-orange-100 text-orange-900 dark:bg-orange-900 dark:text-orange-200",
    closeBtn: "text-orange-900 dark:text-orange-200",
  },
  grey: {
    container: "bg-grey-100 text-grey-900 dark:bg-grey-800 dark:text-grey-200",
    closeBtn: "text-grey-900 dark:text-grey-200",
  },
};

export default function Toast({
  color,
  text,
  additionalText,
  isFullWidth = false,
  onClose,
  closable = true,
  autoClose = true,
  autoCloseDuration = 10000,
  action,
}: Props) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    setIsVisible(true);

    if (autoClose) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, autoCloseDuration);

      return () => clearTimeout(timer);
    }
  }, [text, color, onClose, autoClose, autoCloseDuration]);

  if (!isVisible) return null;

  return (
    <div
      className={`px-3 py-2 ${colorClasses[color].container} radius-sm flex items-center gap-4 ${isFullWidth ? "w-full" : "min-w-2xs"}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="shrink-0">
              {color === "positive" && <CheckIcon />}
              {color === "negative" && <InfoCircle size={16} color="currentColor" />}
              {color === "grey" && <InfoCircle size={16} color="currentColor" />}
              {color === "notice" && <Warning2 size={16} color="currentColor" />}
            </span>
            <p className="text-label-sm">{text}</p>
          </div>
          {additionalText && <p className="text-body-regular-xs pl-6 whitespace-pre-line">{additionalText}</p>}
        </div>
        {action && (
          <Button
            variant="secondary"
            size="sm"
            onClick={action.onClick}
            className="shrink-0 absolute -right-2 -top-1.5">
            {action.label}
          </Button>
        )}
      </div>
      {closable && (
        <button
          className={`${colorClasses[color].closeBtn} cursor-pointer shrink-0 self-start`}
          onClick={() => {
            setIsVisible(false);
            onClose?.();
          }}>
          <CloseIcon />
        </button>
      )}
    </div>
  );
}
